// src/pages/RichTextEditorPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import { getRequest, postRequest, putRequest } from "../api/api";
import {
  Button,
  message,
  Upload,
  Card,
  Divider,
  Typography,
  Space,
  Collapse,
} from "antd";
import {
  UploadOutlined,
  EyeOutlined,
  CodeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

const { Title } = Typography;
const { Panel } = Collapse;

const RichTextEditorPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isNew = id === "new";

  const queryParams = new URLSearchParams(location.search);
  const [editorHtml, setEditorHtml] = useState("");
  const [previewVisible, setPreviewVisible] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [chapterId] = useState(queryParams.get("chapter_id"));
  const [order] = useState(queryParams.get("order") || 1);
  const [imageUrl] = useState(
    queryParams.get("image_url") || "https://via.placeholder.com/60"
  );
  const [animationType] = useState(queryParams.get("animation_type") || "Fade");
  const [imagePosition] = useState(queryParams.get("image_position") || "T");
  const [txtFile, setTxtFile] = useState(null);

  useEffect(() => {
    if (!isNew) fetchSlideContent();
  }, [id]);

  const fetchSlideContent = async () => {
    const data = await getRequest(`chapter-slides/${id}`);
    setEditorHtml(data?.content || "");
  };

  const handleTxtUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const rawHtml = e.target.result;
      setEditorHtml(rawHtml); // set raw HTML from file directly as content
    };
    reader.readAsText(file);
    setTxtFile(file);
    return false;
  };

  const downloadHtmlAsTxt = () => {
    const element = document.createElement("a");
    const file = new Blob([editorHtml], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `slide-${id === "new" ? "draft" : id}.txt`;
    document.body.appendChild(element);
    element.click();
  };

  const handleSave = async () => {
    if (!chapterId || !order || !editorHtml) {
      return message.error("All fields are required.");
    }

    const payload = {
      chapter_id: Number(chapterId),
      order: String(order),
      image_url: imageUrl,
      animation_type: animationType,
      image_position: imagePosition,
      is_active: true,
      content: editorHtml,
    };

    try {
      if (isNew) {
        await postRequest("chapter-slides", payload);
        message.success("Slide created successfully");
      } else {
        await putRequest(`chapter-slides/${id}`, payload);
        message.success("Slide updated successfully");
      }
      navigate("/chapter-slides");
    } catch (err) {
      message.error("Error while saving slide");
    }
  };

  return (
    <div style={{ padding: 30 }}>
      <Title level={3}>
        {isNew ? "Create New Slide" : `Update Slide #${id}`}
      </Title>

      <Space style={{ marginBottom: 20 }} size="middle" wrap>
        <Upload
          beforeUpload={handleTxtUpload}
          showUploadList={false}
          accept=".txt"
        >
          <Button icon={<UploadOutlined />}>Upload .txt</Button>
        </Upload>

        <Button
          icon={<EyeOutlined />}
          onClick={() => setPreviewVisible(!previewVisible)}
        >
          {previewVisible ? "Hide Preview" : "Show Preview"}
        </Button>

        <Button icon={<CodeOutlined />} onClick={() => setShowCode(!showCode)}>
          {showCode ? "Hide Code" : "Show Code"}
        </Button>

        <Button icon={<DownloadOutlined />} onClick={downloadHtmlAsTxt}>
          Download .txt
        </Button>

        <Button type="primary" onClick={handleSave}>
          Save
        </Button>
      </Space>

      <Divider />
      <SunEditor
        height="400px"
        onChange={setEditorHtml}
        setContents={editorHtml} // ✅ Always reflects latest content changes
        setOptions={{
          defaultStyle: "font-family: Arial; font-size: 16px;",
          buttonList: [
            [
              "undo",
              "redo",
              "font",
              "fontSize",
              "formatBlock",
              "paragraphStyle",
              "blockquote",
              "bold",
              "underline",
              "italic",
              "strike",
              "subscript",
              "superscript",
              "fontColor",
              "hiliteColor",
              "textStyle",
              "removeFormat",
              "outdent",
              "indent",
              "align",
              "horizontalRule",
              "list",
              "lineHeight",
              "table",
              "link",
              "image",
              "video",
              "audio",
              "fullScreen",
              "showBlocks",
              "codeView",
              "preview",
              "print",
            ],
          ],
        }}
        onFocus={(e) => {
          // 📌 Optional: Ensure selection is tracked
          window.lastRange = window.getSelection()?.getRangeAt(0);
        }}
        onBlur={(e) => {
          // 📌 Optional: Restore selection for consistent tool behavior
          if (window.lastRange) {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(window.lastRange);
          }
        }}
      />

      {previewVisible && (
        <Card
          title="Live Preview"
          style={{ marginTop: 20, background: "#fafafa" }}
          bordered
        >
          <div dangerouslySetInnerHTML={{ __html: editorHtml }} />
        </Card>
      )}

      {showCode && (
        <Card title="HTML Code" style={{ marginTop: 20 }} bordered>
          <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
            {editorHtml}
          </pre>
        </Card>
      )}
    </div>
  );
};

export default RichTextEditorPage;
